import { gql } from 'graphql-request';
import {
    Customer,
    PaginationInput,
    PaginationOutput,
    ProjectFile,
    SamplingCadence,
    Site,
    Project,
    ProjectFilters,
    ProjectDefinition,
} from 'src/shared/types';

export const projectsQuery = gql`
    query getProjectList($customerId: String!) {
        customerWithProjects: customer(customerId: $customerId) {
            projects {
                projectId
                projectName
                projectType
                samplesCount
                subscription {
                    name
                    label
                    allowedResources
                }
                features
                samplingCadence {
                    type
                }
                status
            }
        }
    }
`;
export type CustomerProject = Pick<
    ProjectDefinition,
    'projectId' | 'projectName' | 'projectCode' | 'samplesCount' | 'subscription' | 'features' | 'projectType' | 'status'
> & {
    samplingCadence: Pick<SamplingCadence, 'type'>;
};

export type CustomerProjects = CustomerProject[];

export type ProjectsQueryResult = {
    customerWithProjects: {
        projects: CustomerProjects;
    };
};

export const projectForAdminQuery = gql`
    query getProjectList($customerId: String!) {
        customer(customerId: $customerId) {
            projects {
                projectId
                projectName
                projectCode
                country
                createdAt
                creatorName
                status
                samplesCount
                habitatAssay {
                    habitatName
                    assayName
                    habitatAssayKey
                }
                features
                updatedAt
                updaterName
            }
        }
    }
`;

export type ProjectForAdmin = Pick<
    ProjectDefinition,
    | 'projectCode'
    | 'projectName'
    | 'habitatAssay'
    | 'country'
    | 'createdAt'
    | 'creatorName'
    | 'projectId'
    | 'status'
    | 'samplesCount'
    | 'features'
    | 'updatedAt'
    | 'updaterName'
>;

export type ProjectsForAdminQueryResult = {
    customer: {
        projects: ProjectForAdmin[];
    };
};

export type HabitatAssay = {
    habitatName?: string | null;
    assayName?: string | null;
    habitatAssayKey?: string | null;
};

export type ProjectCardData = {
    projectId: string;
    projectName: string;
    projectCode: string;
    country?: string | null;
    habitatAssays?: HabitatAssay[] | null;
    features: string[];
    subscription: { name: string };
    samplesCount: number;
    projectStartDate?: string | null;
    status: string;
    createdAt?: string | null;
    updatedAt?: string | null;
};

/** Alias for user-management and other callers that still import this name. */
export type BasicProjectListItem = ProjectCardData;

/** Unified list row from `projectList` (My sites + Project admin). */
export type ProjectListItem = BasicProjectListItem;

export type AdminProjectListItem = BasicProjectListItem;

export type ProjectListData = {
    projects: ProjectCardData[];
    pagination: PaginationOutput;
};

export type ProjectListResponse = {
    projects: {
        projectList: ProjectListData;
    };
};

export type ProjectListInput = {
    customerId: string;
    pagination: PaginationInput;
    searchText?: string;
};

const projectListRowFields = `
    projectId
    projectName
    projectCode
    country
    status
    habitatAssays {
        habitatName
        assayName
        habitatAssayKey
    }
    features
    subscription {
        name
    }
    samplesCount
    projectStartDate
    createdAt
    updatedAt
`;

export const projectListQuery = gql`
    query getProjectList($input: ProjectListInput!) {
        projects {
            projectList(input: $input) {
                projects {
                    ${projectListRowFields}
                }
                pagination {
                    page
                    pageSize
                    total
                }
            }
        }
    }
`;

export type UserManagementProjectListInput = {
    customerId: string;
    pagination: PaginationInput;
    searchText?: string;
};

export type UserManagementProjectListResponse = {
    usersQueries: {
        userManagementProjectList: ProjectListData;
    };
};

export type UserCountLimitation = {
    max: number;
    softLimit?: number | null;
};

export type UserCountByRolesRow = {
    name: string;
    roleCount?: number | null;
    limitation: UserCountLimitation;
};

export type UserListSummaryRow = {
    projectId?: string | null;
    countByRoles: UserCountByRolesRow[];
};

export type UserCountSummaryQueryResult = {
    userCountSummary: UserListSummaryRow[];
};

export type UserCountSummaryQueryParams = {
    projectIds: string[];
    includeDisabledUser: boolean;
};

export const userCountSummaryQuery = gql`
    query getUserCountSummary($projectIds: [String], $includeDisabledUser: Boolean!) {
        userCountSummary(projectIds: $projectIds, includeDisabledUser: $includeDisabledUser) {
            projectId
            countByRoles {
                name
                roleCount
                limitation {
                    max
                    softLimit
                }
            }
        }
    }
`;

export const userManagementProjectListQuery = gql`
    query getUserManagementProjectList($input: UserManagementProjectListInput!) {
        usersQueries {
            userManagementProjectList(input: $input) {
                projects {
                    ${projectListRowFields}
                }
                pagination {
                    page
                    pageSize
                    total
                }
            }
        }
    }
`;

/** Note: the risk-portfolio site import reads `samples { location { coordinates } }`
 *  from this query to locate sites for projects that have no habitat sites.
 *  Trimming that selection would silently stop those sites getting a location —
 *  see `admin/views/risk-portfolio/helpers/representativePoint.ts`. */
export const projectQuery = gql`
    query projects($projectId: String!) {
        project(projectId: $projectId) {
            projectId
            projectName
            projectStartDate
            latestReportingDate
            griisLookupCountries
            samples {
                location {
                    coordinates
                }
                sampleId
                sampleCollectedDatetime
                sampleDeployDatetime
            }
        }
    }
`;

export type ProjectQueryResult = {
    project: Project;
};

export const projectFiltersQuery = gql`
    query getFilters($projectId: String!) {
        filters(projectId: $projectId) {
            map {
                times
                areas
                habitatAssays
                hlcaSites
                hlcaChartTypes
            }
        }
    }
`;

export type ProjectFiltersQueryResult = {
    filters: ProjectFilters;
};

export const customerProjectsInBCQuery = gql`
    query getCustomertListWithProjects($input: CustomerProjectsInBCInput!) {
        customerProjectsInBC(input: $input) {
            customerId
            customerName
            projects {
                projectId
                projectName
                projectCode
                leadName
                leadEmail
            }
        }
    }
`;

export const enablePdfDownloadQuery = gql`
    mutation ($projectId: String!, $eventName: String!) {
        admin {
            enablePdfDownload(projectId: $projectId, eventName: $eventName)
        }
    }
`;

export type CustomerProjectsInBCQueryResult = {
    customerProjectsInBC: Pick<Customer, 'customerId' | 'customerName' | 'projects'>;
};

export type GetProjectSamplingCadenceQueryResult = {
    getProjectSamplingCadence: {
        samplingCadence: SamplingCadence;
    };
};

export const getProjectSamplingCadenceQuery = gql`
    query getProjectSamplingCadence($projectId: String!) {
        getProjectSamplingCadence: getProjectSummary(projectId: $projectId) {
            samplingCadence {
                type
                samplingEventsPerYear
                events {
                    identifier
                    name
                    fromDate
                    toDate
                    year
                    processed
                    processedDate
                    status
                }
            }
        }
    }
`;

export const projectSitesQuery = gql`
    query getProjectSites($projectId: String!) {
        getProjectSites: getProjectSummary(projectId: $projectId) {
            sites {
                type
                uploadedAt
                uploadedBy
                geometry {
                    coordinates
                }
                properties {
                    name
                    area
                    status
                    statusMessage
                    metrics {
                        metric
                        layers {
                            year
                            status
                            url
                            boundaryUrl
                            errorMessage
                        }
                    }
                }
            }
        }
    }
`;

export type ProjectSitesQueryResult = {
    /** Aliased from `getProjectSummary` by `projectSitesQuery`. `sites` is null
     *  for projects that have no habitat sites at all — Species Insights
     *  projects always answer null here. */
    getProjectSites: {
        sites: Site[] | null;
    };
};

export const projectDefinitionQuery = gql`
    query getProjectDefinition($projectId: String!) {
        projectDefinition: getProjectSummary(projectId: $projectId) {
            projectId
            customerName
            projectName
            projectCode

            subscription {
                label
                name
                allowedResources
            }
            country
            leadEmail
            leadName
            leadRole
            projectType
            samplingCadence {
                type
                samplingEventsPerYear
                events {
                    identifier
                    name
                    fromDate
                    toDate
                    year
                    processed
                    processedDate
                    status
                }
            }
            files(fileType: "reports") {
                name
            }
            projectLifespanYears
            sampleGroups
            habitatAssay {
                habitatName
                assayName
                habitatAssayKey
            }
            projectEndDate
            projectStartDate
            estimatedSampleCounts
            estimatedSampleCountForTestTypes {
                type {
                    assayName
                    habitatAssayKey
                    habitatName
                }
                count
            }
            createdAt
            updatedAt
            samplesCount
            features
            griisLookupCountries
            isChegdProject
            notifyUsersOnChange
            hasDefinedSampleEvents
            status
        }
    }
`;

export type ProjectDefinitionQueryResult = Pick<
    ProjectDefinition,
    | 'projectId'
    | 'customerName'
    | 'projectName'
    | 'projectCode'
    | 'subscription'
    | 'country'
    | 'leadEmail'
    | 'leadName'
    | 'leadRole'
    | 'projectType'
    | 'samplingCadence'
    | 'projectLifespanYears'
    | 'sampleGroups'
    | 'habitatAssay'
    | 'projectStartDate'
    | 'projectEndDate'
    | 'estimatedSampleCounts'
    | 'estimatedSampleCountForTestTypes'
    | 'createdAt'
    | 'updatedAt'
    | 'samplesCount'
    | 'features'
    | 'griisLookupCountries'
    | 'isChegdProject'
    | 'notifyUsersOnChange'
    | 'hasDefinedSampleEvents'
    | 'status'
> & {
    files: Pick<ProjectFile, 'name'>[];
};

export const deleteProjectDefinitionQuery = gql`
    mutation deleteProjectDefinition($projectId: String!) {
        deleteProjectDefinition(projectId: $projectId)
    }
`;

export const hasActiveEdnaDataReuploadRequestQuery = gql`
    query hasActiveEdnaDataReuploadRequest($projectId: String!) {
        admin {
            hasActiveEdnaDataReuploadRequest(projectId: $projectId) {
                status
                data {
                    requestId
                    requestedBy
                    requestor
                    status
                    createdAt
                    errorDetails
                    reviewData {
                        file
                        habitat
                        assay
                        samplesCount
                        sampleCollectedDateRange {
                            from
                            to
                        }
                        samplesReceivedDateRange {
                            from
                            to
                        }
                    }
                }
                errorDetails
            }
        }
    }
`;

export const getLastEdnaDataReuploadQuery = gql`
    query getLastEdnaDataReupload($projectId: String!) {
        admin {
            getLastEdnaDataReupload(projectId: $projectId) {
                requestId
                status
                approvedAt
                approvedBy
                createdAt
            }
        }
    }
`;

export type EdnaDataReuploadReviewItemDateRange = {
    from: string;
    to: string;
};

export type EdnaDataReuploadReviewItem = {
    file: string;
    habitat: string;
    assay: string;
    samplesCount: number;
    sampleCollectedDateRange: EdnaDataReuploadReviewItemDateRange;
    samplesReceivedDateRange: EdnaDataReuploadReviewItemDateRange;
};

export type EdnaDataReuploadRequestData = {
    requestId: string;
    requestedBy: string;
    requestor?: string | null;
    status: string;
    createdAt: string;
    approvedAt?: string | null;
    approvedBy?: string | null;
    errorDetails?: string | null;
    reviewData?: EdnaDataReuploadReviewItem[] | null;
};

export type EdnaDataReuploadRequestStatus = {
    status: string;
    data?: EdnaDataReuploadRequestData | null;
    errorDetails?: string | null;
};

export type HasActiveEdnaDataReuploadRequestResult = {
    admin: {
        hasActiveEdnaDataReuploadRequest: EdnaDataReuploadRequestStatus | null;
    };
};

export type GetLastEdnaDataReuploadResult = {
    admin: {
        getLastEdnaDataReupload: EdnaDataReuploadRequestData | null;
    };
};

export const requestEdnaDataReuploadReviewMutation = gql`
    mutation requestEdnaDataReuploadReview($projectId: String!) {
        admin {
            requestEdnaDataReuploadReview(projectId: $projectId)
        }
    }
`;

export type RequestEdnaDataReuploadReviewResult = {
    admin: {
        requestEdnaDataReuploadReview: boolean;
    };
};

export const approveEdnaDataReuploadMutation = gql`
    mutation approveEdnaDataReupload($requestId: String!) {
        admin {
            approveEdnaDataReupload(requestId: $requestId)
        }
    }
`;

export type ApproveEdnaDataReuploadResult = {
    admin: {
        approveEdnaDataReupload: boolean;
    };
};

export const dismissEdnaDataReuploadMutation = gql`
    mutation dismissEdnaDataReupload($requestId: String!) {
        admin {
            dismissEdnaDataReupload(requestId: $requestId)
        }
    }
`;

export type DismissEdnaDataReuploadResult = {
    admin: {
        dismissEdnaDataReupload: boolean;
    };
};
